/**
 * isProjectActive — canonical "is this project still in scope for reporting
 * obligations?" predicate.
 *
 * The `project_status` enum is: Proposed · Planned · In Progress · Abandoned ·
 * Completed (see projectStandardOptions.ts). All non-terminal statuses qualify
 * because legitimate data work happens at every pre-execution phase too:
 *   - Proposed:   feasibility data, baseline measurements, scoping inputs
 *   - Planned:    pre-mobilization preflight items, partner sign-offs,
 *                 resource readiness reporting
 *   - In Progress: ongoing actuals during execution
 *
 * Only the terminal statuses are filtered out:
 *   - Abandoned: project cancelled; no further reporting expected
 *   - Completed: project finished; no further reporting expected
 *
 * Used by surfaces that should only act on non-terminal projects:
 *   - The Action Center Data tab (DEV-681) — Abandoned and Completed projects
 *     don't generate data entry obligations.
 *   - The DEV-529 bell trigger — same rule; we don't want users pinged about
 *     projects that have been closed out.
 *
 * Mirrors isProjectCompleted's override-aware lookup: the project config
 * override's `project_status` takes precedence over the persisted value,
 * matching the app's established override precedence.
 *
 * NOTE: This is a UI/business-rule predicate, intentionally distinct from
 * the data-reliability engine's `isNowApplicable()` lifecycle gate. The
 * engine considers project age and completion; this util is a simpler
 * "is the project still active enough to need attention?" check.
 */

import type { ProjectConfigOverride } from './projectConfigOverrides';

/** Permissive — accepts the row, the row's nested .project, or both. */
export interface ProjectLikeForActive {
  project_status?: string | null;
  project?: {
    project_status?: string | null;
  } | null;
}

/** Statuses where the project is closed out and no longer reporting. */
const TERMINAL_STATUSES = new Set<string>(['ABANDONED', 'COMPLETED']);

export function isProjectActive(
  project: ProjectLikeForActive | null | undefined,
  override: ProjectConfigOverride | null | undefined,
): boolean {
  const statusRaw =
    override?.project_status
    ?? project?.project_status
    ?? project?.project?.project_status
    ?? '';
  const status = String(statusRaw).trim().toUpperCase();
  // Defensive: an empty status (e.g., a freshly created row that hasn't
  // been status-set yet) is treated as active — better to surface than
  // hide silently. Only explicit terminal statuses are filtered out.
  if (!status) return true;
  return !TERMINAL_STATUSES.has(status);
}
